"use client";

/**
 * AvatarSelection 컴포넌트
 * @description 아바타 하나를 고르는 컨트롤. 앱 DS `AvatarSelection` 의 웹 포팅.
 * @example
 * <AvatarSelection title={artist.name} imageUrl={artist.logo} fallbackUrl={FALLBACK}
 *   selected={picked} onToggle={toggle} />
 */
import { Check } from "lucide-react";
import Image from "next/image";
import { memo } from "react";

import {
  AVATAR_SELECTION_BADGE_SIZE,
  AVATAR_SELECTION_SIZE,
  avatarSelectionStyles,
} from "./AvatarSelection.styles";
import type { AvatarSelectionProps } from "./AvatarSelection.types";

const CHECK_SIZE = 16;

const AvatarSelection = memo<AvatarSelectionProps>(
  ({
    title,
    imageUrl,
    fallbackUrl,
    selected = false,
    disabled = false,
    onToggle,
  }) => {
    const state = disabled ? "disabled" : selected ? "selected" : "rest";

    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-label={title}
        disabled={disabled}
        onClick={onToggle}
        className={`group ${avatarSelectionStyles.root}`}
      >
        <span className={avatarSelectionStyles.frame}>
          <Image
            src={imageUrl || fallbackUrl}
            alt=""
            aria-hidden
            width={AVATAR_SELECTION_SIZE}
            height={AVATAR_SELECTION_SIZE}
            className={avatarSelectionStyles.image}
            style={{
              width: AVATAR_SELECTION_SIZE,
              height: AVATAR_SELECTION_SIZE,
            }}
          />
          <span aria-hidden className={avatarSelectionStyles.ring({ state })} />
          {selected && !disabled ? (
            <span
              aria-hidden
              className={avatarSelectionStyles.badge}
              style={{
                width: AVATAR_SELECTION_BADGE_SIZE,
                height: AVATAR_SELECTION_BADGE_SIZE,
              }}
            >
              <Check size={CHECK_SIZE} strokeWidth={2} />
            </span>
          ) : null}
        </span>

        <span className={avatarSelectionStyles.title({ state })}>{title}</span>
      </button>
    );
  },
);

AvatarSelection.displayName = "AvatarSelection";

export default AvatarSelection;
